import { useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  useParagraphImages,
  useUnlinkImage,
  type ParagraphImageDto,
} from '../../api/queries/media';
import { AppImage } from '../common/AppImage';
import { ImageLightbox } from '../common/ImageLightbox';
import { ParagraphAudioIndicator } from './ParagraphAudioIndicator';
import { ImageInsertPopover } from './ImageInsertPopover';
import { ImageContextMenu } from './ImageContextMenu';

interface ParagraphInlineMediaProps {
  paragraphId: string;
  onGenerateImage: () => void;
  onGenerateAudio?: () => void;
}

export function ParagraphInlineMedia({
  paragraphId,
  onGenerateImage,
  onGenerateAudio,
}: ParagraphInlineMediaProps) {
  const { data } = useParagraphImages(paragraphId);
  const unlinkImage = useUnlinkImage(paragraphId);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const hasImages = data && data.length > 0;

  const handleGenerateAudio = onGenerateAudio ?? (() => {});

  if (!hasImages) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          '&:hover .add-image-btn': { opacity: 0.6 },
        }}
      >
        <ImageInsertPopover
          paragraphId={paragraphId}
          onGenerateImage={onGenerateImage}
        />
        <ParagraphAudioIndicator
          paragraphId={paragraphId}
          onGenerateAudio={handleGenerateAudio}
        />
      </Stack>
    );
  }

  // Group images by position type.
  const bannerImages = data.filter((i) => i.position === 'banner');
  const backgroundImages = data.filter((i) => i.position === 'background');
  const inlineImages = data.filter(
    (i) => !i.position || i.position === 'inline',
  );

  const lightboxImages = data.map((ref) => ({
    src: ref.url,
    alt: 'Paragraph image',
  }));

  return (
    <Box sx={{ mt: 0.5, mb: 0.5 }}>
      {/* Banner images — full-width above paragraph */}
      {bannerImages.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1 }}>
          {bannerImages.map((ref) => (
            <ImageWithRemove
              key={ref.referenceId}
              image={ref}
              onRemove={() => unlinkImage.mutate(ref.referenceId)}
              onClick={() =>
                setLightboxIndex(
                  data.findIndex((d) => d.referenceId === ref.referenceId),
                )
              }
              variant="banner"
            />
          ))}
        </Stack>
      )}

      {/* Background images — shown as a visual indicator */}
      {backgroundImages.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1 }}>
          {backgroundImages.map((ref) => (
            <ImageWithRemove
              key={ref.referenceId}
              image={ref}
              onRemove={() => unlinkImage.mutate(ref.referenceId)}
              onClick={() =>
                setLightboxIndex(
                  data.findIndex((d) => d.referenceId === ref.referenceId),
                )
              }
              variant="background"
            />
          ))}
        </Stack>
      )}

      {/* Inline images — horizontal thumbnail strip */}
      {inlineImages.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {inlineImages.map((ref) => (
            <ImageWithRemove
              key={ref.referenceId}
              image={ref}
              onRemove={() => unlinkImage.mutate(ref.referenceId)}
              onClick={() =>
                setLightboxIndex(
                  data.findIndex((d) => d.referenceId === ref.referenceId),
                )
              }
              variant="inline"
            />
          ))}
        </Stack>
      )}

      <ImageLightbox
        open={lightboxIndex >= 0}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(-1)}
        onIndexChange={setLightboxIndex}
      />
      <Box sx={{ mt: 0.5 }}>
        <ParagraphAudioIndicator
          paragraphId={paragraphId}
          onGenerateAudio={handleGenerateAudio}
        />
      </Box>
    </Box>
  );
}

interface ImageWithRemoveProps {
  image: ParagraphImageDto;
  onRemove: () => void;
  onClick: () => void;
  onRegenerate?: () => void;
  variant: 'inline' | 'banner' | 'background';
}

function ImageWithRemove({
  image,
  onRemove,
  onClick,
  onRegenerate,
  variant,
}: ImageWithRemoveProps) {
  const inner = renderImageVariant(image, onRemove, onClick, variant);
  return (
    <ImageContextMenu onRegenerate={onRegenerate} onRemove={onRemove}>
      {inner}
    </ImageContextMenu>
  );
}

function renderImageVariant(
  image: ParagraphImageDto,
  onRemove: () => void,
  onClick: () => void,
  variant: 'inline' | 'banner' | 'background',
) {
  if (variant === 'banner') {
    return (
      <Box
        sx={{
          position: 'relative',
          '&:hover .unlink-btn': { opacity: 1 },
        }}
      >
        <AppImage
          src={image.url}
          width="100%"
          objectFit="cover"
          objectPosition="center"
          sx={{ borderRadius: 1, maxHeight: 300, cursor: 'pointer' }}
          onClick={onClick}
        />
        <PositionBadge label="Banner" />
        <RemoveButton onRemove={onRemove} />
      </Box>
    );
  }

  if (variant === 'background') {
    return (
      <Box
        sx={{
          position: 'relative',
          '&:hover .unlink-btn': { opacity: 1 },
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <AppImage
          src={image.url}
          width="100%"
          objectFit="cover"
          objectPosition="center"
          sx={{
            maxHeight: 180,
            filter: 'brightness(0.5)',
            cursor: 'pointer',
          }}
          onClick={onClick}
        />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            color: 'white',
            fontWeight: 600,
          }}
        >
          Background image
        </Typography>
        <PositionBadge label="BG" />
        <RemoveButton onRemove={onRemove} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        '&:hover .unlink-btn': { opacity: 1 },
      }}
    >
      <AppImage
        src={image.thumbnailUrl ?? image.url}
        height={120}
        width={160}
        objectPosition="top center"
        objectFit="cover"
        onClick={onClick}
      />
      <RemoveButton onRemove={onRemove} />
    </Box>
  );
}

function RemoveButton({ onRemove }: { onRemove: () => void }) {
  return (
    <IconButton
      className="unlink-btn"
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      sx={{
        position: 'absolute',
        top: 2,
        right: 2,
        bgcolor: 'rgba(0,0,0,0.5)',
        color: 'white',
        opacity: 0,
        transition: 'opacity 0.2s',
        p: 0.25,
        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
      }}
    >
      <CloseIcon sx={{ fontSize: 14 }} />
    </IconButton>
  );
}

function PositionBadge({ label }: { label: string }) {
  return (
    <Typography
      variant="caption"
      sx={{
        position: 'absolute',
        top: 4,
        left: 4,
        bgcolor: 'rgba(0,0,0,0.6)',
        color: 'white',
        px: 0.75,
        py: 0.25,
        borderRadius: 0.5,
        fontSize: 10,
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
  );
}
